/**
 * Desktop download contract.
 *
 * The Apps nav and `/download` both read this file so a release
 * bump is an edit to the MAC_* constants in one place. macOS artifacts are
 * published through the public, metadata-only `okracomputer/desktop-releases`
 * repository. The canonical source repository is private, so visitor-facing
 * download and release-note links must never point at it.
 *
 * Sizes and checksums are copied from the published release assets. If they
 * drift, the download page lies about what the user is about to run — treat
 * them as part of the release checklist, not decoration.
 */

const PUBLIC_DESKTOP_RELEASES_REPO = 'https://github.com/okracomputer/desktop-releases';

// Final URL: docs.okrapdf.com/cli answers with a 307 to /cli/quickstart, which
// Ahrefs flags as "Page has links to redirect" on / and /download. [X.0.43]
export const DESKTOP_CLI_DOCS_URL = 'https://docs.okrapdf.com/cli/quickstart';

/**
 * Shared `/download` copy for React and the crawler-readable Worker response.
 * The page is the desktop app's home (W.4.107): the privacy-first pitch that
 * was the apex landing (W.4.93), and the installers.
 */
export const DOWNLOAD_META = {
  title: 'Download Okra — Privacy-first document extraction for Mac',
  description:
    'Free desktop app and CLI for Mac: OCR, table extraction, and redaction with local models. Every result links to its source. No account. Windows waitlist open.',
  canonical: 'https://okrapdf.com/download',
  h1: 'Privacy-first document extraction.',
  /** The paragraph under the heading, word for word in React and in the crawler body. */
  lede:
    'Pull text, tables, and data out of PDFs with OCR that runs on your own computer. Redact sensitive details before anything leaves your desk. Every result links back to the exact spot on the page.',
} as const;

export interface DesktopBuild {
  /** Platform label shown on the card. */
  platform: string;
  /** Hardware/OS requirement, one line. */
  requirement: string;
  version: string;
  tag: string;
  /** Direct asset URL — a click starts the download. */
  href: string;
  /** Human-readable artifact size. */
  size: string;
  /** File name of the asset, so the page can name what lands in Downloads. */
  file: string;
  sha256: string;
  releaseNotesUrl: string;
  /** Signing/distribution status, stated plainly. */
  signing: string;
  /** Release channel, used for the badge on the card. */
  channel: 'release candidate' | 'alpha';
  /** What the build actually does, in one sentence. */
  summary: string;
  /** Install steps, in order. */
  steps: string[];
  /** In-page anchor on `/download` so the Apps nav can deep-link a card. */
  anchor: 'macos' | 'windows';
}

/**
 * A Mac release bump edits these three values. The tag, file name and URLs
 * derive from the version so they cannot drift apart.
 */
const MAC_VERSION = '1.0.0-rc.55';
const MAC_SIZE = '57.4 MB';
const MAC_SHA256 = '260c885d918b91c55e5f2f4991f955bc7a87630d71cdd888a8a59995e2b164b9';

const MAC_TAG = `desktop-v${MAC_VERSION}`;
const MAC_FILE = `Okra-${MAC_VERSION}.dmg`;

export const MAC_BUILD: DesktopBuild = {
  platform: 'macOS · Apple silicon',
  requirement: 'Apple silicon Mac, macOS 13 or later',
  version: MAC_VERSION,
  tag: MAC_TAG,
  href: `${PUBLIC_DESKTOP_RELEASES_REPO}/releases/download/${MAC_TAG}/${MAC_FILE}`,
  size: MAC_SIZE,
  file: MAC_FILE,
  sha256: MAC_SHA256,
  releaseNotesUrl: `${PUBLIC_DESKTOP_RELEASES_REPO}/releases/tag/${MAC_TAG}`,
  signing: 'Developer ID signed, hardened, notarized by Apple, and stapled',
  channel: 'release candidate',
  summary:
    'Open and edit a PDF, choose a model, inspect source-linked blocks and tables, then approve redactions and export a separate copy.',
  anchor: 'macos',
  steps: [
    'Download the DMG and open it.',
    'Drag Okra onto the Applications shortcut, then eject the DMG.',
    'Open Okra from Applications — it opens normally under Gatekeeper.',
    `Optional: verify the download with shasum -a 256 -c ${MAC_FILE}.sha256.`,
  ],
};

export const DESKTOP_BUILDS: DesktopBuild[] = [MAC_BUILD];

export const ALL_RELEASES_URL = `${PUBLIC_DESKTOP_RELEASES_REPO}/releases`;

/**
 * Install-page destination. W.4.72 can also select the desktop-app action as
 * the public header/hero primary CTA; the Apps nav deep-links here regardless.
 */
export const PUBLIC_DOWNLOAD_CTA = {
  label: 'Download',
  href: '/download',
} as const;

export interface AppNavItem {
  id: 'macos' | 'windows' | 'chrome';
  title: string;
  body: string;
  href: string;
}

/** Middle-nav Apps dropdown: installable clients, not the account CTA. */
export const APP_NAV_ITEMS: AppNavItem[] = [
  {
    id: 'macos',
    title: 'Desktop for Mac',
    body: `${MAC_BUILD.requirement}. ${MAC_BUILD.channel}.`,
    href: `${PUBLIC_DOWNLOAD_CTA.href}#${MAC_BUILD.anchor}`,
  },
  {
    id: 'windows',
    title: 'Desktop for Windows',
    body: 'Coming soon. Join the email waitlist.',
    href: `${PUBLIC_DOWNLOAD_CTA.href}#windows`,
  },
  {
    id: 'chrome',
    title: 'Chrome extension',
    body: 'Markdown, tables and an accessibility check for the PDF in your tab.',
    href: '/chrome-extension',
  },
];
